/**
 * Split a completed chat turn's transcript into multiple readable bubbles.
 *
 * A single speaking turn (assistant or candidate) often arrives as one long
 * blob — assistant speech can be a big run-on paragraph, and candidate
 * speech-to-text is usually one continuous string with little/no punctuation.
 * Rendering that as a single bubble reads poorly. This breaks a turn into
 * several bubbles so long turns look clean:
 *
 *   1. Split on hard paragraph breaks (blank line, `\n\n`).
 *   2. Within each paragraph, group sentences ~SENTENCES_PER_BUBBLE at a time.
 *   3. Fallback for unpunctuated speech (no sentence enders): chunk by word
 *      count so a wall of text still breaks up.
 *
 * Short turns (a few sentences) collapse back to a single bubble naturally.
 * Purely presentational — callers apply it at render time, so it never touches
 * the stored messages, persistence, transcript snapshots, or rejoin history.
 */
// Sentences grouped per bubble when a paragraph runs long.
export const SENTENCES_PER_BUBBLE = 4;
// A paragraph shorter than this is always kept as ONE bubble (no over-splitting
// of short turns), regardless of sentence count.
const KEEP_WHOLE_UNDER_CHARS = 240;
// Fallback: words per bubble when a long chunk has no sentence boundaries
// (typical of unpunctuated candidate speech-to-text).
const WORDS_PER_BUBBLE_FALLBACK = 45;
function chunkByWords(text, wordsPerChunk) {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length <= wordsPerChunk)
        return [text.trim()];
    const chunks = [];
    for (let i = 0; i < words.length; i += wordsPerChunk) {
        chunks.push(words.slice(i, i + wordsPerChunk).join(' '));
    }
    return chunks;
}
/**
 * Break `text` into an ordered list of bubble strings. Returns `[text]` for
 * short/empty input so callers can always map over the result.
 */
export function isSplitChatBubblesEnabled() {
    var _a;
    // Default ON; set REACT_APP_SPLIT_CHAT_BUBBLES_ENABLED=false to roll back to
    // one-bubble-per-turn rendering.
    const raw = ((_a = process.env.REACT_APP_SPLIT_CHAT_BUBBLES_ENABLED) !== null && _a !== void 0 ? _a : '').trim().toLowerCase();
    return !['false', '0', 'no', 'off'].includes(raw);
}
export function splitIntoBubbles(text) {
    const raw = (text !== null && text !== void 0 ? text : '').trim();
    if (!raw)
        return [];
    // Flag off → keep the whole turn as a single bubble (legacy behavior).
    if (!isSplitChatBubblesEnabled())
        return [raw];
    const paragraphs = raw
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean);
    const bubbles = [];
    for (const para of paragraphs) {
        // Short paragraph → keep whole.
        if (para.length <= KEEP_WHOLE_UNDER_CHARS) {
            bubbles.push(para);
            continue;
        }
        // Split into sentences, keeping the terminal punctuation.
        const sentences = para
            .split(/(?<=[.!?])\s+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (sentences.length <= 1) {
            // No sentence boundaries (e.g. unpunctuated speech) → word-count chunks.
            bubbles.push(...chunkByWords(para, WORDS_PER_BUBBLE_FALLBACK));
            continue;
        }
        for (let i = 0; i < sentences.length; i += SENTENCES_PER_BUBBLE) {
            bubbles.push(sentences.slice(i, i + SENTENCES_PER_BUBBLE).join(' '));
        }
    }
    return bubbles.length ? bubbles : [raw];
}
